import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Avatar } from "@heroui/react";
import clsx from "clsx";
import {
  useAdvanceMutation,
  useAssignRoleMutation,
  useCloseInvitesMutation,
  useGetGameQuery,
  useGetRolesQuery,
  useRsvpMutation,
  useStartGameMutation,
} from "../../reducers/apiSlice";
import { useToast } from "../../components/ui/toast/index";
import convertErrorMessage from "../../utils/convertErrorMessage";
import { useLoginState } from "../../hooks/login";
import { useOpenLoginModal } from "../../components/Modals/loginModalContext";
import type { Game, GameRole, Mystery, Player } from "../../types/game";
import { GameState } from "../../types/game";
import GameInProgressView from "./GameInProgressView";
import CharacterReveal from "./CharacterReveal";
import { useVisiblePolling, useRefreshOnReturn } from "./useLiveRefresh";
/* UI-DESIGN: ported lobby's case-file design language onto this page;
   .module.scss replaces the engineer's .module.css (project rule = scss only) */
import StatePanel from "../../components/lightbox/StatePanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import styles from "./GamePage.module.scss";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import { frameFor } from "../../components/lightbox/frames";
import { initialTypeface } from "../../components/lightbox/typefaces";

/* UI-DESIGN: LB-55 the planning side of the game page (the invite guests
   land on, the host's event page, loading and errors) sits in the same lit
   room as Home, Create Game and the lobby. The playing side
   (GameInProgressView) now shares the same room after LB-83. */
const noop = () => undefined;
const Room = ({ children }: { children: ReactNode }) => (
  <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={noop}>
    {children}
  </LightboxSurface>
);

const RSVP_GOING = "going";
const RSVP_DROPPED = "dropped";
const HOST_INSTRUCTIONS_URL =
  "https://puzzlestorage221.blob.core.windows.net/publictesting/Host%20Instructions.pdf";

/* UI-DESIGN: LB-55 the lightbox's own few, as in the lobby */
const AVATAR_COLORS = [
  "oklch(0.9 0.03 85)",
  "oklch(0.78 0.08 75)",
  "oklch(0.74 0.15 22)",
  "oklch(0.72 0.03 250)",
  "oklch(0.74 0.07 195)",
] as const;

function hostDisplayName(host: Game["host"]): string {
  if (!host) return "Your host";
  const name = [host.first_name, host.last_name].filter(Boolean).join(" ").trim();
  return name || "Your host";
}

function guestInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

function mysteryImageUrl(mystery: Mystery | undefined): string | null {
  if (!mystery) return null;
  if (mystery.image_url) return mystery.image_url;
  const sets = (mystery as Mystery & { mystery_sets?: { image_url?: string }[] }).mystery_sets;
  return sets?.find((set) => set.image_url)?.image_url ?? "https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Ftse4.mm.bing.net%2Fth%2Fid%2FOIP.ZpJCQLRS26DNmMljNfkYAQHaHa%3Fpid%3DApi&f=1&ipt=87fea426d1bd1d7ced62228c06449c057159bb5ec4ecb29c542f19001b213ccd";
}

interface GuestRowProps {
  showContact: boolean;
  player: Player;
  /* UI-DESIGN: hosts pick roles by anonymous group designation during the
     invites-closed window; open roles come pre-filtered from the page */
  roleOptions?: GameRole[];
  onAssignRole?: (playerId: string, roleId: string | null) => void;
  assigningRoles?: boolean;
}

function GuestRow({ player, showContact, roleOptions, onAssignRole, assigningRoles }: GuestRowProps) {
  const going = player.attendance;
  const showRoleSelect = roleOptions != null && onAssignRole != null && going;

  /* open roles + the player's currently-held role stay selectable */
  const selectableRoles = showRoleSelect
    ? roleOptions.filter(
        (role) =>
          role.assigned_player_id === null ||
          role.assigned_player_id === player.objectId
      )
    : [];

  return (
    <li className={styles.guestRow}>
      <Avatar size="sm" className={styles.guestAvatar}>
        <Avatar.Fallback
          className={styles.guestAvatarFallback}
          style={{ background: avatarColor(player.name) }}
        >
          {guestInitials(player.name)}
        </Avatar.Fallback>
      </Avatar>
      <div className={styles.guestInfo}>
        <p className={styles.guestName}>{player.name}</p>
        {/* UI-DESIGN: LB-92 only hosts need guest contact details. */}
        {showContact && player.email ? <p className={styles.guestEmail}>{player.email}</p> : null}
      </div>
      {showRoleSelect ? (
        <select
          className={styles.roleSelect}
          aria-label={`Role for ${player.name}`}
          value={player.role?.objectId ?? ""}
          disabled={assigningRoles}
          onChange={(e) => onAssignRole(player.objectId, e.target.value || null)}
        >
          <option value="">Unassigned</option>
          {selectableRoles.map((role) => (
            <option key={role.objectId} value={role.objectId}>
              {role.anonymous_display}
            </option>
          ))}
        </select>
      ) : null}
      <span
        className={`${styles.statusChip} ${going ? styles.statusGoing : styles.statusNotGoing}`}
      >
        <span className={styles.statusDot} aria-hidden />
        {going ? "Going" : "Not Going"}
      </span>
    </li>
  );
}

function GamePageLoading() {
  // UI-DESIGN: quiet status matches Open Cases without a large animated skeleton.
  return <Room><div className={styles.page}><StatePanel aria-busy="true" aria-label="Loading game"><p role="status">Opening your case…</p></StatePanel></div></Room>;
}

const GamePage = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const pollingInterval = useVisiblePolling();
  const { currentData: game, isLoading, isError: refreshFailed, refetch } = useGetGameQuery(gameId!, {
    skip: !gameId, pollingInterval, refetchOnMountOrArgChange: true,
  });
  useRefreshOnReturn(refetch, Boolean(gameId));
  const [advanceGame, { isLoading: isAdvancing }] = useAdvanceMutation();
  const [rsvp, { isLoading: isRsvping }] = useRsvpMutation();
  const [startGame, { isLoading: isSendingRoles }] = useStartGameMutation();
  const [closeInvites, { isLoading: isClosingInvites }] = useCloseInvitesMutation();
  const [assignRole, { isLoading: isAssigningRole }] = useAssignRoleMutation();
  const { triggerToast } = useToast();
  /* UI-DESIGN: 2026-10-25 (Taco) a playing host should be able to stay as much
     in the dark as the guests; null = not chosen yet, default from host_is_playing */
  const [pickRolesChoice, setPickRolesChoice] = useState<boolean | null>(null);
  const { isLoggedIn } = useLoginState();
  const openLoginModal = useOpenLoginModal();

  /* UI-DESIGN: role picking is a host-only, invites-closed-only affair — one
     fetch, rows filter the open roles client-side */
  const canEditRoles =
    game?.is_host === true && game?.game_state === GameState.INVITES_CLOSED;
  const { data: gameRoles, refetch: refetchRoles, isLoading: rolesLoading, isError: rolesError } = useGetRolesQuery(gameId!, {
    skip: !gameId || !canEditRoles,
  });

  const submitRsvpGoing = (): Promise<void> => {
    if (!gameId) return Promise.resolve();
    return rsvp({ game: gameId, status: RSVP_GOING })
      .unwrap()
      .then(() => {
        triggerToast({ title: "Success", description: "You’re on the list!" });
        void refetch();
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  const handleRsvp = () => {
    if (!gameId) return;
    if (isLoggedIn) {
      void submitRsvpGoing();
      return;
    }
    openLoginModal({
      uponVerificationComplete: submitRsvpGoing,
    });
  };

  /* Guests who already RSVP'ed can flip to "dropped" while invites are open */
  const handleRsvpDropped = () => {
    if (!gameId) return;
    rsvp({ game: gameId, status: RSVP_DROPPED })
      .unwrap()
      .then(() => {
        triggerToast({
          title: "RSVP updated",
          description: "You’ve been marked as not attending.",
        });
        void refetch();
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  const handleCloseInvites = () => {
    if (!gameId) return;
    closeInvites({ game_id: gameId })
      .unwrap()
      .then(() => {
        triggerToast({
          title: "Invites closed",
          description: "The roster is locked — you can now assign roles.",
        });
        void refetch();
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  const handleSendRolesOut = () => {
    if (!gameId) return;
    startGame({ game_id: gameId })
      .unwrap()
      .then(() => {
        triggerToast({
          title: "Roles sent",
          description: "Emails have been sent to all players.",
        });
        void refetch();
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  const handleAssignRole = (playerId: string, roleId: string | null) => {
    assignRole({ target_player: playerId, target_role: roleId })
      .unwrap()
      .then(() => {
        /* assignment can steal a role from another row, so both refresh */
        void refetch();
        void refetchRoles();
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  // UI-DESIGN: use the existing round transition only after roles are sent.
  const handleStartRound = () => {
    if (!gameId) return;
    advanceGame({ game_id: gameId }).unwrap().then(() => {
      triggerToast({ title: "Game started", description: "Round one is ready." });
      void refetch();
    }).catch((err) => triggerToast({ title: "Error", description: convertErrorMessage(err) }));
  };

  const handleInviteGuest = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      triggerToast({
        title: "Link copied",
        description: "Share this link to invite guests.",
      });
    } catch {
      triggerToast({
        title: "Invite link",
        description: url,
      });
    }
  };

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!gameId) {
    return <Navigate to="/" replace />;
  }

  if (isLoading && !game) {
    return <GamePageLoading />;
  }

  if (!game) {
    return (
      <Room>
      <div className={styles.page}>
        <StatePanel className={styles.errorPanel}>
          <p>Game not found or could not be loaded.</p>
          <div className={styles.errorActions}>
            <LightboxButton type="button" onPress={() => void refetch()}>
              Try again
            </LightboxButton>
            <Link to="/">Go home</Link>
          </div>
        </StatePanel>
      </div>
      </Room>
    );
  }

  if (game.game_state === GameState.IN_PROGRESS) {
    return <GameInProgressView key={game.objectId} game={game} />;
  }

  const mystery = game.mystery;
  /* UI-DESIGN: LB-55 the case's own file (the homepage frame) when there is
     one, so the invite shows the same object the host picked */
  const caseFrame = frameFor(mystery?.name);
  const imageUrl = caseFrame ?? mysteryImageUrl(mystery);
  const isHost = game.is_host === true;
  const hasGoingRsvp = game.rsvp?.status === RSVP_GOING;
  const hostName = hostDisplayName(game.host);
  const players = game.players ?? [];
  const attendingCount = players.filter((p) => p.attendance).length;
  const isInviting = game.game_state === GameState.INVITING;
  const isInvitesClosed = game.game_state === GameState.INVITES_CLOSED;
  const isPreGame = game.game_state === GameState.PRE_GAME;
  const canSendRoles = isHost && isInvitesClosed;
  const currentStep = isInviting ? 0 : isInvitesClosed ? 1 : 2;
  // UI-DESIGN: LB-82 roles stay sealed until sent, on both game entry points.
  const pickRoles = pickRolesChoice ?? game.host_is_playing === false;
  const chooseShuffle = () => {
    setPickRolesChoice(false);
    // UI-DESIGN: shuffling means nobody was hand-picked, so clear earlier picks
    for (const p of players) if (p.role?.objectId) handleAssignRole(p.objectId, null);
  };
  const characterReveal = (
    <CharacterReveal
      key={`${game.objectId}:${game.my_character?.objectId}:${game.my_character?.role?.objectId}`}
      character={game.my_character}
      gameId={game.objectId}
      hostNotPlaying={isHost && game.host_is_playing === false}
      inline
      emphasis={!isHost}
    />
  );
  /* UI-DESIGN: hosts see case identity beside the event title; guests keep the inline art. */
  const caseArt = (placement: string) => (
    <div className={clsx(styles.imageWrap, placement)}>
      {imageUrl ? (
        <img
          className={clsx(styles.image, caseFrame && styles.imageFile)}
          src={imageUrl}
          alt={mystery?.name ? `${mystery.name} artwork` : "Mystery artwork"}
          loading="eager"
        />
      ) : (
        <div className={styles.imageFallback} aria-hidden />
      )}
    </div>
  );

  const guestActions = !isHost ? (
    <div className={styles.guestActions}>
      {/* UI-DESIGN: 2026-10-25 (Taco) shorter, and clear that the wait is for the host's roles */}
      {hasGoingRsvp && <p className={styles.waiting} role="status">{isInviting ? "You're in. Your character arrives when the host sends roles." : isInvitesClosed ? "The guest list is locked. Your host is handing out roles." : "Roles are out. Open yours, then wait for round one."}</p>}

      {!hasGoingRsvp && !isInviting ? (
        /* UI-DESIGN: roster is frozen after close-invites — no late RSVPs */
        <p className={styles.invitesClosedNote}>
          Invites are closed for this game — the guest list has been locked in.
        </p>
      ) : !hasGoingRsvp ? (
        <button
          type="button"
          className={styles.actionCardPrimary}
          onClick={handleRsvp}
          disabled={isRsvping}
        >
          <span className={styles.actionCardTitle}>
            {isRsvping ? "Saving…" : (
              <>RSVP &mdash; I&rsquo;m going <span className="ui-arrow" aria-hidden>→</span></>
            )}
          </span>
          <span className={styles.actionCardSubtext}>
            Let the host know you&apos;re coming to this mystery.
          </span>
        </button>
      ) : (
        <>
          {isPreGame && characterReveal}
          {/* UI-DESIGN: one red action per screen; instructions are a quiet link, as for the host */}
          <button type="button" className={styles.quietAction} onClick={() => navigate("/about")}>How to play</button>

          {/* UI-DESIGN: guests can back out after RSVP'ing, but only while
              invites are open \u2014 the roster locks at close-invites */}
          {isInviting ? (
            <button
              type="button"
              className={`${styles.actionCardSecondary} ${styles.actionCardDanger}`}
              onClick={handleRsvpDropped}
              disabled={isRsvping}
            >
              <span className={styles.actionCardTitle}>
                {isRsvping ? "Saving\u2026" : "I can no longer make it"}
              </span>
              <span className={styles.actionCardSubtext}>
                Let the host know your plans changed. You can RSVP again while
                invites are open.
              </span>
            </button>
          ) : null}
        </>
      )}
    </div>
  ) : null;

  return (
    <Room>
    <div className={styles.page}>
        {refreshFailed && <p role="status">Couldn’t refresh the game. Showing the last received information. <button type="button" className={styles.quietAction} onClick={() => void refetch()}>Try again</button></p>}

      <div className={styles.layout}>
        <GlassPanel
          as="section"
          className={styles.eventPanel}
          aria-labelledby="game-event-heading"
        >

          <div className={styles.eventHeading}>
            {isHost && caseArt(styles.imageHeading)}
            <h1 id="game-event-heading" className={styles.gameTitle}>
              {game.name}
            </h1>
            {!isHost ? (
              <p className={styles.hostInviteLine}>
                <strong>{hostName}</strong> invites you
              </p>
            ) : null}
          </div>

          {!isHost && mystery?.description ? (
            <p className={styles.description}>{mystery.description}</p>
          ) : null}

          {isHost ? (
            <div className={styles.hostActions}>
              {/* UI-DESIGN: one current step, one primary transition, before the artwork. */}
              <ol className={styles.steps} aria-label="Host progress">
                {["Invite guests", "Assign roles", "Start game"].map((step, index) => (
                  <li key={step} aria-current={currentStep === index ? "step" : undefined} className={clsx(index < currentStep && styles.stepDone)}>
                    <span aria-hidden>{index < currentStep ? "✓" : index + 1}</span>{step}
                  </li>
                ))}
              </ol>
              <h2 className={styles.stepTitle}>{isInviting ? "Gather your guests" : isInvitesClosed ? "Hand out the roles" : "Ready for round one"}</h2>
              {!isInvitesClosed && <p className={styles.stepCopy}>
                {isInviting ? "Share the invite link. Close invites once everyone has replied." : "Roles are out. Start when everyone's ready."}
              </p>}
              {isInvitesClosed && (
                <fieldset className={styles.roleChoice} disabled={isAssigningRole || isSendingRoles}>
                  <legend className={styles.srOnly}>How roles are handed out</legend>
                  <label className={clsx(styles.roleOption, !pickRoles && styles.roleOptionOn)}>
                    <input type="radio" name="role-mode" checked={!pickRoles} onChange={chooseShuffle} />
                    <span><strong>Shuffle for everyone</strong>You find out with your guests. Best if you’re playing.</span>
                  </label>
                  <label className={clsx(styles.roleOption, pickRoles && styles.roleOptionOn)}>
                    <input type="radio" name="role-mode" checked={pickRoles} onChange={() => setPickRolesChoice(true)} />
                    <span><strong>Pick who plays what</strong>Choose in the guest list. Labels hide the characters.</span>
                  </label>
                </fieldset>
              )}
              {isInvitesClosed && pickRoles && <a className={styles.roleLink} href="#game-guests-heading">Choose roles in the guest list ↓</a>}
              {/* UI-DESIGN: sharing is primary; closing invites is secondary. */}
              {isInviting && <button type="button" className={styles.actionCardPrimary} onClick={() => void handleInviteGuest()}><span className={styles.actionCardTitle}>Copy invite link</span></button>}
              {isInviting ? (
                <button type="button" className={styles.actionCardSecondary} onClick={handleCloseInvites} disabled={isClosingInvites}>
                  <span className={styles.actionCardTitle}>{isClosingInvites ? "Closing…" : "Close invites"}</span>
                </button>
              ) : canSendRoles ? (
                <button type="button" className={styles.actionCardPrimary} onClick={handleSendRolesOut} disabled={isSendingRoles || isAssigningRole}>
                  <span className={styles.actionCardTitle}>{isSendingRoles ? "Sending…" : "Send roles out"}</span>
                </button>
              ) : isPreGame ? (
                <button type="button" className={styles.actionCardPrimary} onClick={handleStartRound} disabled={isAdvancing}>
                  <span className={styles.actionCardTitle}>{isAdvancing ? "Starting…" : "Start round one"}</span>
                </button>
              ) : null}
              {isPreGame && characterReveal}
              <div className={styles.hostSecondaryRow}>
                <button type="button" className={styles.quietAction} onClick={() => openExternal(HOST_INSTRUCTIONS_URL)}>Host instructions</button>
                <button type="button" className={styles.quietAction} onClick={() => navigate("/about")}>How to play</button>
              </div>
            </div>
          ) : null}

          {!isHost && caseArt(styles.imageInline)}

          {guestActions}
        </GlassPanel>

        <GlassPanel
          as="aside"
          className={styles.guestPanel}
          aria-labelledby="game-guests-heading"
        >
          <div className={styles.guestPanelHeader}>
            <div className={styles.guestPanelHeading}>
              <h2 id="game-guests-heading" className={styles.guestPanelTitle}>
                Guest list
              </h2>
              {isHost ? (
                <p className={styles.guestPanelHint}>
                  {isInviting ? "RSVPs appear here as guests reply." : isInvitesClosed ? (pickRoles ? "Labels hide who the characters are." : "Roles are shuffled when you send them.") : "The guest list is locked."}
                </p>
              ) : null}
            </div>

            <div className={styles.guestPanelMeta}>
              <div className={styles.attendanceStats}>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.statValueGoing}`}>
                    {attendingCount}
                  </span>
                  <span className={styles.statLabel}>Attending</span>
                </div>
                <div className={styles.statCard}>
                  <span className={`${styles.statValue} ${styles.statValueMaybe}`}>{players.length - attendingCount}</span>
                  <span className={styles.statLabel}>Not attending</span>
                </div>
              </div>
            </div>
          </div>

          {canEditRoles && rolesLoading && <p className={styles.stepCopy} role="status">Loading role choices…</p>}
          {canEditRoles && rolesError && <div className={styles.waiting}><p>Role choices couldn't load.</p><button type="button" className={styles.quietAction} onClick={() => void refetchRoles()}>Try roles again</button></div>}
          {players.length > 0 ? (
            <>
              <div className={styles.guestListHeader}>
                <span>Name/Email</span>
                <span>{canEditRoles ? "Role / Status" : "Status"}</span>
              </div>
              <ul className={styles.guestList}>
                {players.map((player) => (
                  <GuestRow
                    key={player.objectId}
                    player={player}
                    showContact={isHost}
                    roleOptions={canEditRoles && pickRoles ? gameRoles : undefined}
                    onAssignRole={canEditRoles && pickRoles ? handleAssignRole : undefined}
                    assigningRoles={isAssigningRole || isSendingRoles}
                  />
                ))}
              </ul>
            </>
          ) : (
            /* UI-DESIGN: empty state with a little flavor instead of bare text */
            <p className={styles.emptyGuests}>
              No suspects on the list yet. Share the game link and watch the
              guest list fill in here.
            </p>
          )}
        </GlassPanel>
      </div>
    </div>
    </Room>
  );
};

export default GamePage;
